/**
 * Tour Mappings Configuration
 * Maps Rezdy product codes to booking URLs and tour type classifications
 */

export type TourType = 'Full Day' | 'Half Day' | 'Summit' | 'Private' | 'Other';

export interface TourMapping {
  productCode: string;
  bookingUrl: string;
  tourType: TourType;
  displayName?: string; // Optional override for display name
}

// Manual mapping of Rezdy product codes to booking page URLs
export const tourMappings: Record<string, TourMapping> = {
  // Premium Tongariro Crossing
  'PAMGPM': {
    productCode: 'PAMGPM',
    bookingUrl: 'https://adriftnz.rezdy.com/123189/premium-tongariro-crossing-guided-group-walk',
    tourType: 'Full Day'
  },
  
  // Coach Tour Tongariro Crossing
  'PPPGWJ': {
    productCode: 'PPPGWJ',
    bookingUrl: 'https://adriftnz.co.nz/tongariro-crossing/',
    tourType: 'Full Day'
  },

  // Basic Mt Ruapehu Summit Plateau
  'PLM0PT': {
    productCode: 'PLM0PT',
    bookingUrl: 'https://adriftnz.co.nz/mt-ruapehu-summit-plateau/',
    tourType: 'Summit'
  },

  // Premium Mt Ruapehu Summit Plateau
  'PYU02T': {
    productCode: 'PYU02T',
    bookingUrl: 'https://adriftnz.rezdy.com/128306/premium-mt-ruapehu-summit-plateau-guided-group-hike',
    tourType: 'Summit'
  },

  // Half Day Tongariro Alpine
  'PNNMHD': {
    productCode: 'PNNMHD',
    bookingUrl: 'https://adriftnz.rezdy.com/128315/half-day-tongariro-alpine-guided-group-walk',
    tourType: 'Half Day'
  },

  // Private Half Day Tongariro Alpine
  'PRWMYA': {
    productCode: 'PRWMYA',
    bookingUrl: 'https://adriftnz.rezdy.com/241863/private-half-day-tongariro-alpine-guided-hike',
    tourType: 'Private'
  },

  // 2 Hour Guided Day Walk
  'PYFZ1Y': {
    productCode: 'PYFZ1Y',
    bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/',
    tourType: 'Half Day'
  },

  // 2 Hour Pre Sunset Walk
  'PY3W0J': {
    productCode: 'PY3W0J',
    bookingUrl: 'https://adriftnz.rezdy.com/128316/2-hour-pre-sunset-guided-group-walk',
    tourType: 'Half Day'
  },

  // Tama Lakes Guided Walk
  'P3WAFJ': {
    productCode: 'P3WAFJ',
    bookingUrl: 'https://adriftnz.rezdy.com/454592/private-tama-lakes-guided-walk',
    tourType: 'Full Day'
  },

  // Private Tongariro Crossing
  'PWX8QA': {
    productCode: 'PWX8QA',
    bookingUrl: 'https://adriftnz.rezdy.com/128319/private-tongariro-crossing-guided-walk',
    tourType: 'Private'
  },

  // Private Mt Ruapehu Summit
  'P1WMB9': {
    productCode: 'P1WMB9',
    bookingUrl: 'https://adriftnz.rezdy.com/128306/premium-mt-ruapehu-summit-plateau-guided-group-hike',
    tourType: 'Private'
  },

  // 3 Day Northern Circuit
  'PMURTJ': {
    productCode: 'PMURTJ',
    bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/',
    tourType: 'Full Day'
  },

  // 3 Day Whanganui River Canoe
  'P12LKN': {
    productCode: 'P12LKN',
    bookingUrl: 'https://adriftnz.co.nz/guided-whanganui-canoeing/',
    tourType: 'Full Day'
  },

  // 1 Day Whanganui River Canoe (Coach Tour)
  'PALZZY': {
    productCode: 'PALZZY',
    bookingUrl: 'https://adriftnz.co.nz/guided-whanganui-canoeing/',
    tourType: 'Full Day'
  },

  // 1 Day Whanganui River Canoe (Private)
  'PAHHJW': {
    productCode: 'PAHHJW',
    bookingUrl: 'https://adriftnz.rezdy.com/315858/private-1-day-whanganui-river-guided-canoe-tour-taumarunui-to-ohinepane',
    tourType: 'Private'
  },

  // Winter Only Tongariro Crossing
  'PQS1HE': {
    productCode: 'PQS1HE',
    bookingUrl: 'https://adriftnz.co.nz/tongariro-crossing/',
    tourType: 'Full Day'
  },

  // TOURPLAN variants
  'PKW9NR': {
    productCode: 'PKW9NR',
    bookingUrl: 'https://adriftnz.co.nz/sunset-short-walk/',
    tourType: 'Half Day'
  },
  'PBNAZR': {
    productCode: 'PBNAZR',
    bookingUrl: 'https://adriftnz.co.nz/half-day-hike/',
    tourType: 'Half Day'
  },
  'PFKWDF': {
    productCode: 'PFKWDF',
    bookingUrl: 'https://adriftnz.co.nz/mt-ruapehu-summit-plateau/',
    tourType: 'Summit'
  },
  'P8AQNX': {
    productCode: 'P8AQNX',
    bookingUrl: 'https://adriftnz.co.nz/tongariro-crossing/',
    tourType: 'Full Day'
  },
  'PE1LTA': {
    productCode: 'PE1LTA',
    bookingUrl: 'https://adriftnz.co.nz/tongariro-crossing/',
    tourType: 'Full Day'
  },
  'PRN9DS': {
    productCode: 'PRN9DS',
    bookingUrl: 'https://adriftnz.co.nz/tongariro-crossing/',
    tourType: 'Private'
  },

  // Cheeky Midweeky
  'PC2DVV': {
    productCode: 'PC2DVV',
    bookingUrl: 'https://adriftnz.rezdy.com/406204/cheeky-midweeky',
    tourType: 'Other'
  }
};

/**
 * Get booking URL for a product code
 */
export function getBookingUrl(productCode: string): string {
  const mapping = tourMappings[productCode];
  if (mapping) {
    return mapping.bookingUrl;
  }
  // Fallback to main guided walks page
  return 'https://adriftnz.co.nz/guided-tongariro-walks/';
}

/**
 * Get tour type for a product code
 */
export function getTourType(productCode: string): TourType {
  const mapping = tourMappings[productCode];
  if (mapping) {
    return mapping.tourType;
  }
  // Default to Other if not mapped
  return 'Other';
}

/**
 * Check if a product code is a Tongariro Crossing tour
 */
export function isTongariroCrossing(productCode: string): boolean {
  const mapping = tourMappings[productCode];
  return mapping !== undefined && mapping.bookingUrl.includes('tongariro-crossing');
}

/**
 * Check if a product should be displayed in availability checker
 * Filters out gift cards, gear hire, and other non-tour products
 */
export function shouldDisplayProduct(productName: string, productCode: string): boolean {
  const nameLower = productName.toLowerCase();
  
  // Exclude these types
  if (nameLower.includes('gift card')) return false;
  if (nameLower.includes('gear hire')) return false;
  if (nameLower.includes('packed lunch')) return false;
  if (nameLower.includes('accommodation') || nameLower.includes('chalets')) return false;
  if (nameLower.includes('transfer surcharge')) return false;
  if (nameLower.includes('guide only')) return false;
  if (nameLower.includes('school camps')) return false;
  if (nameLower.includes('transport only')) return false;
  
  return true;
}

/**
 * Get all available tour types for filtering
 */
export const tourTypeFilters: TourType[] = ['Full Day', 'Half Day', 'Summit', 'Private'];


